/**
 * Approvals Queue Page
 * Review and approve pending requests (expenses, time-off, purchase orders)
 */
import React, { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  CheckCircle, XCircle, Clock, DollarSign, Calendar, ShoppingCart,
  User, Building2, AlertTriangle, Check, X, ChevronRight
} from 'lucide-react';
import { timeOffRequests } from '@/data/staff';
import { cn } from '@/lib/utils';

// Mock expense requests
const expenseRequests = [
  {
    id: 'exp-001',
    type: 'expense',
    category: 'Vedlikehold',
    description: 'Tandoor reparasjon - utskifting av varmeelement',
    amount: 4500,
    currency: 'NOK',
    submittedBy: 'Rajesh Kumar',
    submittedDate: '2025-01-26',
    outletId: 'no-sorengkaia',
    outletName: 'Sørengkaia',
    status: 'pending',
    priority: 'high',
    attachments: 1,
    notes: 'Tandooren sluttet å fungere under lunsjtjeneste. Trenger godkjenning for reparasjon.',
  },
  {
    id: 'exp-002',
    type: 'expense',
    category: 'Markedsføring',
    description: 'Instagram sponset innlegg - Januar kampanje',
    amount: 2500,
    currency: 'NOK',
    submittedBy: 'Line Johansen',
    submittedDate: '2025-01-25',
    outletId: 'no-sorengkaia',
    outletName: 'Sørengkaia',
    status: 'pending',
    priority: 'normal',
    attachments: 2,
  },
  {
    id: 'exp-003',
    type: 'expense',
    category: 'Utstyr',
    description: 'Nye serveringsbrett (10 stk)',
    amount: 1800,
    currency: 'NOK',
    submittedBy: 'Magnus Berg',
    submittedDate: '2025-01-24',
    outletId: 'no-sorengkaia',
    outletName: 'Sørengkaia',
    status: 'pending',
    priority: 'low',
    attachments: 0,
  },
];

// Mock purchase orders
const purchaseOrders = [
  {
    id: 'po-001',
    type: 'purchase',
    supplier: 'Engros Grønt AS',
    description: 'Ukentlig grønnsaksleveranse',
    amount: 12500,
    currency: 'NOK',
    submittedBy: 'Line Johansen',
    submittedDate: '2025-01-26',
    outletId: 'no-sorengkaia',
    outletName: 'Sørengkaia',
    status: 'pending',
    priority: 'high',
    deliveryDate: '2025-01-28',
    items: 15,
  },
  {
    id: 'po-002',
    type: 'purchase',
    supplier: 'Asko Norge',
    description: 'Månedlig tørrvarer',
    amount: 28000,
    currency: 'NOK',
    submittedBy: 'Kristian Svendsen',
    submittedDate: '2025-01-25',
    outletId: 'no-frogner',
    outletName: 'Frogner',
    status: 'pending',
    priority: 'normal',
    deliveryDate: '2025-01-30',
    items: 42,
  },
];

export function ApprovalsPage() {
  const [activeTab, setActiveTab] = useState('all');

  // Combine all pending items
  const allPendingItems = useMemo(() => {
    const expenses = expenseRequests.filter(e => e.status === 'pending');
    const orders = purchaseOrders.filter(o => o.status === 'pending');
    const timeOff = timeOffRequests.filter(t => t.status === 'pending').map(t => ({
      ...t,
      type: 'timeoff',
      amount: t.days,
      currency: 'dager',
      submittedBy: t.employeeName,
      submittedDate: t.requestedDate.split('T')[0],
      description: `${t.type === 'vacation' ? 'Ferie' : 'Sykdom'}: ${t.reason}`,
      priority: 'normal',
    }));
    return [...expenses, ...orders, ...timeOff].sort((a, b) => {
      const priorityOrder = { high: 0, normal: 1, low: 2 };
      return priorityOrder[a.priority] - priorityOrder[b.priority];
    });
  }, []);

  // Stats
  const stats = useMemo(() => ({
    total: allPendingItems.length,
    expenses: expenseRequests.filter(e => e.status === 'pending').length,
    purchases: purchaseOrders.filter(o => o.status === 'pending').length,
    timeOff: timeOffRequests.filter(t => t.status === 'pending').length,
    highPriority: allPendingItems.filter(i => i.priority === 'high').length,
    totalValue: allPendingItems
      .filter(i => i.currency === 'NOK')
      .reduce((sum, i) => sum + i.amount, 0),
  }), [allPendingItems]);

  const formatCurrency = (amount, currency = 'NOK') => {
    if (currency === 'dager') return `${amount} dager`;
    return new Intl.NumberFormat('nb-NO', { 
      style: 'currency', 
      currency,
      maximumFractionDigits: 0 
    }).format(amount);
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'expense': return <DollarSign className="w-4 h-4" />;
      case 'purchase': return <ShoppingCart className="w-4 h-4" />;
      case 'timeoff': return <Calendar className="w-4 h-4" />;
      default: return <Clock className="w-4 h-4" />;
    }
  };

  const getTypeLabel = (type) => {
    switch (type) {
      case 'expense': return 'Utgift';
      case 'purchase': return 'Innkjøp';
      case 'timeoff': return 'Fravær';
      default: return type;
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'bg-spice-tandoori text-white';
      case 'low': return 'bg-gray-400 text-white';
      default: return 'bg-spice-turmeric text-white';
    }
  };

  const filteredItems = useMemo(() => {
    if (activeTab === 'all') return allPendingItems;
    return allPendingItems.filter(item => item.type === activeTab);
  }, [activeTab, allPendingItems]);

  return (
    <div data-testid="approvals-page" className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl text-brand-charcoal">Godkjenninger</h1>
          <p className="text-gray-500 text-sm mt-1">
            Gjennomgå og godkjenn ventende forespørsler
          </p>
        </div>
        {stats.highPriority > 0 && (
          <Badge className="bg-spice-tandoori gap-1 text-sm py-1.5 px-3">
            <AlertTriangle className="w-4 h-4" />
            {stats.highPriority} høy prioritet
          </Badge>
        )}
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-5 gap-4">
        <Card className={cn(
          "cursor-pointer transition-all",
          activeTab === 'all' && "ring-2 ring-brand-orange"
        )} onClick={() => setActiveTab('all')}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wide">Totalt ventende</p>
                <p className="text-2xl font-bold text-brand-charcoal">{stats.total}</p>
              </div>
              <Clock className="w-8 h-8 text-brand-orange/20" />
            </div>
          </CardContent>
        </Card>
        <Card className={cn(
          "cursor-pointer transition-all",
          activeTab === 'expense' && "ring-2 ring-brand-orange"
        )} onClick={() => setActiveTab('expense')}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wide">Utgifter</p>
                <p className="text-2xl font-bold text-brand-charcoal">{stats.expenses}</p>
              </div>
              <DollarSign className="w-8 h-8 text-brand-orange/20" />
            </div>
          </CardContent>
        </Card>
        <Card className={cn(
          "cursor-pointer transition-all",
          activeTab === 'purchase' && "ring-2 ring-brand-orange"
        )} onClick={() => setActiveTab('purchase')}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wide">Innkjøp</p>
                <p className="text-2xl font-bold text-brand-charcoal">{stats.purchases}</p>
              </div>
              <ShoppingCart className="w-8 h-8 text-brand-orange/20" />
            </div>
          </CardContent>
        </Card>
        <Card className={cn(
          "cursor-pointer transition-all",
          activeTab === 'timeoff' && "ring-2 ring-brand-orange"
        )} onClick={() => setActiveTab('timeoff')}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wide">Fravær</p>
                <p className="text-2xl font-bold text-brand-charcoal">{stats.timeOff}</p>
              </div>
              <Calendar className="w-8 h-8 text-brand-orange/20" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wide">Total verdi</p>
                <p className="text-2xl font-bold text-spice-cardamom">{formatCurrency(stats.totalValue)}</p>
              </div>
              <DollarSign className="w-8 h-8 text-spice-cardamom/20" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Approval Queue */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-lg flex items-center gap-2">
            <Clock className="w-5 h-5 text-brand-orange" />
            Ventende godkjenninger
            <Badge variant="outline" className="ml-2">{filteredItems.length}</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {filteredItems.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <CheckCircle className="w-12 h-12 mx-auto mb-4 text-spice-cardamom" />
              <p className="text-lg font-medium">Alt er oppdatert!</p>
              <p className="text-sm">Ingen ventende godkjenninger</p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredItems.map(item => (
                <div 
                  key={item.id} 
                  className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-start gap-4">
                    <div className={cn(
                      "w-10 h-10 rounded-lg flex items-center justify-center",
                      item.type === 'expense' ? "bg-blue-100 text-blue-600" :
                      item.type === 'purchase' ? "bg-green-100 text-green-600" :
                      "bg-purple-100 text-purple-600"
                    )}>
                      {getTypeIcon(item.type)}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-xs text-gray-400">{item.id.toUpperCase()}</span>
                        <Badge variant="outline" className="text-xs">
                          {getTypeLabel(item.type)}
                        </Badge>
                        <Badge className={cn("text-xs", getPriorityColor(item.priority))}>
                          {item.priority === 'high' ? 'Høy' : item.priority === 'low' ? 'Lav' : 'Normal'}
                        </Badge>
                      </div>
                      <p className="font-medium text-brand-charcoal mt-1">{item.description}</p>
                      <div className="flex items-center gap-4 mt-1 text-xs text-gray-500">
                        <span className="flex items-center gap-1">
                          <User className="w-3 h-3" /> {item.submittedBy}
                        </span>
                        <span className="flex items-center gap-1">
                          <Building2 className="w-3 h-3" /> {item.outletName || 'Alle'}
                        </span>
                        <span>{item.submittedDate}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <p className="text-lg font-bold font-mono text-brand-charcoal">
                        {formatCurrency(item.amount, item.currency)}
                      </p>
                      {item.deliveryDate && (
                        <p className="text-xs text-gray-500">Levering: {item.deliveryDate}</p>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" className="h-9 bg-spice-cardamom hover:bg-spice-cardamom/90 gap-1">
                        <Check className="w-4 h-4" /> Godkjenn
                      </Button>
                      <Button size="sm" variant="outline" className="h-9 gap-1">
                        <X className="w-4 h-4" /> Avslå
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
